"use client";

import { motion } from "framer-motion";
import { useMemo } from "react";
import { GateGlyph } from "@/components/brand/gate-glyph";
import { AssetIcon } from "@/components/ui/asset-icon";
import { useMotionPreference } from "@/hooks/use-motion-preference";
import { CHAINS } from "@/lib/landing-data";
import { cn } from "@/lib/utils";

/**
 * The cross-chain hub: every chain orbits a central wallet/gate, with glowing
 * "packets" continuously streaming inward along each beam — assets flowing in
 * from every chain. Beams + nodes are laid out in polar coordinates so it's
 * deterministic (no getBoundingClientRect/DOM measuring). Reduced motion keeps
 * the static lines + glow and drops the traveling packets.
 */
export function CrossChainConstellation({ className }: { className?: string }) {
  const { reducedMotion, isMobile } = useMotionPreference();

  const nodes = useMemo(() => {
    const r = 40; // percent of the square's half-extent
    return CHAINS.map((chain, i) => {
      const angle = (i / CHAINS.length) * Math.PI * 2 - Math.PI / 2;
      return { chain, x: 50 + r * Math.cos(angle), y: 50 + r * Math.sin(angle) };
    });
  }, []);

  // One slow revolution of the whole system around the central gate.
  const orbit = { duration: 52, repeat: Infinity, ease: "linear" as const };

  return (
    <div className={cn("relative mx-auto aspect-square w-full max-w-md", className)}>
      {/* Everything but the hub slowly orbits the moongate gate at the center. */}
      <motion.div
        className="absolute inset-0"
        style={{ transformOrigin: "center" }}
        animate={reducedMotion ? undefined : { rotate: 360 }}
        transition={orbit}
      >
        <svg viewBox="0 0 100 100" className="absolute inset-0 size-full" preserveAspectRatio="none">
          {nodes.map((n) => (
            <line
              key={`l-${n.chain.id}`}
              x1={50}
              y1={50}
              x2={n.x}
              y2={n.y}
              stroke="currentColor"
              strokeWidth={0.25}
              className="text-border"
            />
          ))}
          {!reducedMotion && !isMobile &&
            nodes.map((n, i) => (
              <motion.circle
                key={`p-${n.chain.id}`}
                r={1.1}
                fill={n.chain.color}
                initial={{ cx: n.x, cy: n.y, opacity: 0 }}
                animate={{ cx: [n.x, 50], cy: [n.y, 50], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.28, ease: "easeIn" }}
                style={{ filter: `drop-shadow(0 0 2px ${n.chain.color})` }}
              />
            ))}
        </svg>

        {/* Chain nodes ride the orbit; each icon counter-rotates to stay upright. */}
        {nodes.map((n) => (
          <div
            key={`n-${n.chain.id}`}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            <motion.div animate={reducedMotion ? undefined : { rotate: -360 }} transition={orbit}>
              <AssetIcon
                asset={{ symbol: n.chain.name, logo: n.chain.logo, color: n.chain.color }}
                size={40}
                glow
                className="ring-1 ring-border/60"
              />
            </motion.div>
          </div>
        ))}
      </motion.div>

      {/* Center hub — outside the orbit, so the gate stays put and upright */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="relative grid size-20 place-items-center rounded-full border border-primary/40 bg-card/90 shadow-glow md:backdrop-blur-sm">
          <GateGlyph size={20} className="text-primary" animated={false} />
          {!reducedMotion && (
            <motion.span
              className="absolute inset-0 rounded-full border border-primary/40"
              animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
